/**
 * A completed check-in's discussion points, read-only, for its summary (HR UAT
 * list #4): per KPA, what went well, the challenges and the action plan, each
 * point with who added it. The summary used to show only how many there were.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { MessageSquare } from 'lucide-react'
import type { DiscussionPoint } from '@/services/api/reviewService'
import type { KPAData } from '@/types/evidence'
import { discussionPointKeys } from '@/lib/discussionPointKeys'

type Step = 'wentWell' | 'challenges' | 'actions'
type KpaPoints = Record<string, Partial<Record<Step, DiscussionPoint[]>>>

// The 1:1 check-in dialogue's three steps, in its order.
const STEPS: { key: Step; label: string }[] = [
  { key: 'wentWell', label: 'What went well' },
  { key: 'challenges', label: 'Challenges' },
  { key: 'actions', label: 'Action plan, development & support' },
]

/** A KPA's points under any key they may be stored by (see discussionPointKeys), once each. */
function pointsFor(points: KpaPoints, keys: string[], step: Step): DiscussionPoint[] {
  const seen = new Set<string>()
  return keys.flatMap((key) => points[key]?.[step] ?? []).filter((point) => {
    if (point.deleted || seen.has(point.id)) return false
    seen.add(point.id)
    return true
  })
}

export function DiscussionPointsSummary({ kpaData, points }: { kpaData: KPAData[]; points: KpaPoints }) {
  const listed = new Set<string>()
  const groups = kpaData.map((kpa) => {
    const keys = discussionPointKeys(kpa)
    keys.forEach((key) => listed.add(key))
    return { title: kpa.display_name || kpa.name, keys }
  })
  // Points kept under a key none of these KPAs answers to still show, under one heading
  // rather than the raw key (a Team Member saw "1" as a heading, HR UAT 13 Oct 2026).
  const unlisted = Object.keys(points).filter((key) => !listed.has(key))
  if (unlisted.length > 0) groups.push({ title: 'Other points', keys: unlisted })
  const sections = groups
    .map((group) => ({
      title: group.title,
      steps: STEPS.map((step) => ({ ...step, items: pointsFor(points, group.keys, step.key) }))
        .filter((step) => step.items.length > 0),
    }))
    .filter((section) => section.steps.length > 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="h-5 w-5 text-heritage-blue" />
          Discussion points
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {sections.length === 0 ? (
          <p className="text-sm text-gray-500">No discussion points were recorded in this check-in.</p>
        ) : (
          sections.map((section, index) => (
            <section key={index} aria-label={section.title} className="space-y-3">
              <h4 className="font-semibold text-gray-900">{section.title}</h4>
              {section.steps.map((step) => (
                <div key={step.key}>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{step.label}</p>
                  <ul className="space-y-2">
                    {step.items.map((point) => (
                      <li key={point.id} className="flex items-start justify-between gap-3 rounded-md border bg-gray-50 px-3 py-2 text-sm">
                        <span className="whitespace-pre-wrap break-words text-gray-800">{point.content}</span>
                        <Badge
                          variant="outline"
                          className={`shrink-0 text-xs ${
                            point.addedBy === 'employee'
                              ? 'border-blue-200 bg-blue-100 text-blue-700'
                              : 'border-purple-200 bg-purple-100 text-purple-700'
                          }`}
                        >
                          {point.addedBy === 'employee' ? 'Team Member' : 'People Leader'}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ))
        )}
      </CardContent>
    </Card>
  )
}
